import { Plugin, PluginKey } from "@tiptap/pm/state";
import BaseMathematics, {
    defaultShouldRender,
    MathematicsPlugin as BaseMathematicsPlugin,
    MathematicsOptions as BaseMathematicsOptions,
} from "@tiptap-pro/extension-mathematics";
import { Editor } from "@tiptap/core";

export const MATHEMATICS_REGEX = /\$\$(.*?)\$\$/gi;

export type MathematicsOptions = BaseMathematicsOptions & {
    onClick: (
        event: MouseEvent,
        props: {
            editor: Editor;
            /** the raw katex content which was clicked */
            content: string;
            /** The position (start and end) of the katex clicked in editor */
            pos: { start: number; end: number };
        }
    ) => void;
};

export const Mathematics = BaseMathematics.extend<MathematicsOptions>({
    addProseMirrorPlugins() {
        const getEditor = () => this.editor;
        const getOptions = () => this.options;

        return [
            BaseMathematicsPlugin({ ...getOptions(), editor: this.editor }),
            // plugin to control if the katex renderer is clicked
            new Plugin({
                key: new PluginKey("mathematics-click-handler"),
                props: {
                    handleClick(view, _pos, event) {
                        const editor = getEditor();
                        const options = getOptions();

                        let katexRenderer: HTMLElement | null = null;

                        // there could be two cases:
                        // 1. the `target` could directly the katex renderer if the node contain only one katex without any text/marks
                        // wraps it. ex: `$$\frac{1}{2}$$`
                        // 2. the `target` is a normal paragraph node that contains a katex renderer. This happened if
                        // katex in the node is wrapped by text or marks. ex: `Math equation $$\frac{1}{2}$$ of fraction`
                        const target = event.target as HTMLElement | null;

                        if (target?.className.includes("Tiptap-mathematics-editor")) {
                            katexRenderer = target;
                        } else if (target?.children[0]?.className.includes("Tiptap-mathematics-editor")) {
                            katexRenderer = target.children[0] as HTMLElement;
                        }

                        if (katexRenderer) {
                            const pos = view.posAtDOM(katexRenderer, 0);
                            const resolvedPos = view.state.doc.resolve(pos);
                            const node = resolvedPos.nodeAfter;
                            if (node) {
                                const startPos = pos;
                                const endPos = pos + katexRenderer.innerHTML.length;
                                options.onClick(event, {
                                    editor,
                                    content: katexRenderer.innerText,
                                    pos: { start: startPos, end: endPos },
                                });
                            }
                        }
                    },
                },
            }),
        ];
    },

    addOptions() {
        return {
            ...this.parent,
            regex: MATHEMATICS_REGEX,
            shouldRender: (state, pos, _node) => defaultShouldRender(state, pos),
            onClick: () => {},
        };
    },
});
